/**
 * Flow Renderer — 2D directed flowchart layout using SVG
 * Renders a procedure/flowchart with standard shapes (start, process, decision, end),
 * directed edges with arrowheads and labels, branching, and cycle-back edges.
 * Supports [[slug]] wiki-links in step labels — rendered as clickable concept links.
 */

import { navigate } from '../router.js';

const NS = 'http://www.w3.org/2000/svg';

interface FlowColors {
  edge: string;
  edgeHover: string;
  nodeFill: string;
  nodeHover: string;
  nodeStroke: string;
  text: string;
  textDim: string;
  labelBg: string;
  arrowFill: string;
  arrowHover: string;
  startFill: string;
  startStroke: string;
  endStroke: string;
  decisionStroke: string;
}

const COLORS: FlowColors = {
  edge: '#5c584e',
  edgeHover: '#e8e4db',
  nodeFill: '#1a1917',
  nodeHover: '#111110',
  nodeStroke: '#5c584e',
  text: '#e8e4db',
  textDim: '#5c584e',
  labelBg: '#111110',
  arrowFill: '#5c584e',
  arrowHover: '#e8e4db',
  startFill: '#1a1917',
  startStroke: '#FF4F1A',  // signal
  endStroke: '#5c584e',
  decisionStroke: '#C8FF3C', // acid
};

interface ShapeDimensions {
  w: number;
  h: number;
}

const SHAPES: Record<string, ShapeDimensions> = {
  start:    { w: 190, h: 52 },
  end:      { w: 190, h: 52 },
  process:  { w: 210, h: 58 },
  decision: { w: 180, h: 100 },
};

interface LayoutConfig {
  hGap: number;
  vGap: number;
  padding: number;
  backEdgeOffset: number;
}

const LAYOUT: LayoutConfig = {
  hGap: 70,
  vGap: 100,
  padding: 80,
  backEdgeOffset: 40, // horizontal offset for back-edge routing
};

interface LabelSegment {
  type: 'text' | 'link';
  value: string;
}

interface FlowStep {
  id: number;
  label: string;
  shape: string;
  sort_order: number;
}

interface FlowEdge {
  id: number;
  from_step_id: number;
  to_step_id: number;
  label?: string;
}

interface FlowData {
  steps: FlowStep[];
  edges: FlowEdge[];
}

interface FlowRendererOptions {
  onStepClick?: (step: FlowStep) => void;
  conceptMap?: Map<string, string> | null;
}

interface NodePosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Point {
  x: number;
  y: number;
}

export interface FlowRendererInstance {
  highlightSteps(stepIds: number[] | null): void;
  destroy(): void;
}

/**
 * Parse a label string and split it into segments of plain text and [[slug]] links.
 * Returns an array of { type: 'text'|'link', value: string }.
 */
function parseLabelSegments(label: string): LabelSegment[] {
  const segments: LabelSegment[] = [];
  const regex = /\[\[([^\]]+)\]\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(label)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: label.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'link', value: match[1] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < label.length) {
    segments.push({ type: 'text', value: label.slice(lastIndex) });
  }
  return segments;
}

/**
 * Get the plain-text display version of a label (strip [[ ]] brackets).
 * Uses conceptMap to resolve slugs to titles when available.
 */
function stripBrackets(label: string, conceptMap: Map<string, string> | null | undefined): string {
  return label.replace(/\[\[([^\]]+)\]\]/g, (_: string, slug: string) => {
    if (conceptMap && conceptMap.has(slug)) return conceptMap.get(slug)!;
    return slug;
  });
}

/**
 * Render label text with [[slug]] links as clickable SVG tspans.
 * Returns the <text> element. Links are rendered in Signal color and are clickable.
 * conceptMap: Map<slug, title> for resolving display names.
 */
function renderLabelWithLinks(
  label: string,
  x: number,
  y: number,
  fontSize: string,
  maxChars: number,
  conceptMap: Map<string, string> | null | undefined
): SVGTextElement {
  const plainLabel = stripBrackets(label, conceptMap);
  const truncated = plainLabel.length > maxChars;
  const displayLabel = truncated ? plainLabel.slice(0, maxChars - 1) + '\u2026' : plainLabel;

  // If no links or truncated (links don't make sense on truncated text), render plain
  const hasLinks = /\[\[.+?\]\]/.test(label);
  if (!hasLinks || truncated) {
    const text = document.createElementNS(NS, 'text') as SVGTextElement;
    text.setAttribute('x', String(x));
    text.setAttribute('y', String(y));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('fill', COLORS.text);
    text.setAttribute('font-family', "'JetBrains Mono', monospace");
    text.setAttribute('font-size', fontSize);
    text.textContent = displayLabel;
    return text;
  }

  // Render with tspan segments for links
  const segments = parseLabelSegments(label);
  const text = document.createElementNS(NS, 'text') as SVGTextElement;
  text.setAttribute('x', String(x));
  text.setAttribute('y', String(y));
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('font-family', "'JetBrains Mono', monospace");
  text.setAttribute('font-size', fontSize);
  text.setAttribute('fill', COLORS.text);

  for (const seg of segments) {
    const tspan = document.createElementNS(NS, 'tspan') as SVGTSpanElement;
    if (seg.type === 'link') {
      // Show title if available, fall back to slug
      tspan.textContent = (conceptMap && conceptMap.has(seg.value)) ? conceptMap.get(seg.value)! : seg.value;
      tspan.setAttribute('fill', COLORS.startStroke); // Signal color
      tspan.style.cursor = 'pointer';
      tspan.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation();
        navigate(`/concept/${seg.value}`);
      });
      // Underline on hover
      tspan.addEventListener('mouseenter', () => {
        tspan.setAttribute('text-decoration', 'underline');
      });
      tspan.addEventListener('mouseleave', () => {
        tspan.removeAttribute('text-decoration');
      });
    } else {
      tspan.textContent = seg.value;
    }
    text.appendChild(tspan);
  }

  return text;
}

export function createFlowRenderer(
  container: HTMLElement,
  data: FlowData,
  options: FlowRendererOptions = {}
): FlowRendererInstance {
  const { onStepClick = () => {}, conceptMap = null } = options;

  if (!data.steps || data.steps.length === 0) {
    container.innerHTML = '<div style="color: var(--dim); text-align: center; padding: 3rem; font-family: var(--font-system);">No flow steps defined for this concept.</div>';
    return { highlightSteps() {}, destroy() { container.innerHTML = ''; } };
  }

  // --- Build graph adjacency ---
  const stepMap = new Map<number, FlowStep>(); // id -> step
  const outgoing = new Map<number, FlowEdge[]>(); // id -> [edge]
  const incoming = new Map<number, FlowEdge[]>(); // id -> [edge]

  for (const step of data.steps) {
    stepMap.set(step.id, step);
    outgoing.set(step.id, []);
    incoming.set(step.id, []);
  }

  for (const edge of data.edges) {
    if (stepMap.has(edge.from_step_id) && stepMap.has(edge.to_step_id)) {
      outgoing.get(edge.from_step_id)!.push(edge);
      incoming.get(edge.to_step_id)!.push(edge);
    }
  }

  // --- Layer assignment (BFS from start nodes) ---
  // Start nodes: shape=start, or no incoming edges
  const startNodes = data.steps.filter(s =>
    s.shape === 'start' || incoming.get(s.id)!.length === 0
  );
  if (startNodes.length === 0) startNodes.push(data.steps[0]);

  const layer = new Map<number, number>(); // step id -> layer number
  const backEdges = new Set<number>(); // edge ids that are back-edges (cycles)
  const queue: number[] = [];

  for (const s of startNodes) {
    if (!layer.has(s.id)) {
      layer.set(s.id, 0);
      queue.push(s.id);
    }
  }

  // BFS
  let qi = 0;
  while (qi < queue.length) {
    const current = queue[qi++];
    const currentLayer = layer.get(current)!;

    for (const edge of outgoing.get(current)!) {
      const targetId = edge.to_step_id;
      if (layer.has(targetId)) {
        // Already visited — if target is at same or earlier layer, it's a back-edge
        if (layer.get(targetId)! <= currentLayer) {
          backEdges.add(edge.id);
        }
      } else {
        layer.set(targetId, currentLayer + 1);
        queue.push(targetId);
      }
    }
  }

  // Assign unvisited nodes (disconnected) to layer 0
  for (const step of data.steps) {
    if (!layer.has(step.id)) {
      layer.set(step.id, 0);
    }
  }

  // --- Group nodes by layer ---
  const layerCount = Math.max(...[...layer.values()]) + 1;
  const layers: FlowStep[][] = Array.from({ length: layerCount }, () => []);

  for (const step of data.steps) {
    layers[layer.get(step.id)!].push(step);
  }

  // Sort within each layer by sort_order then id
  for (const layerNodes of layers) {
    layerNodes.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  }

  // --- Barycenter ordering (one pass to reduce crossings) ---
  for (let i = 1; i < layers.length; i++) {
    const prevPositions = new Map<number, number>();
    layers[i - 1].forEach((s, idx) => prevPositions.set(s.id, idx));

    layers[i].sort((a, b) => {
      const aParents = incoming.get(a.id)!.map(e => prevPositions.get(e.from_step_id)).filter((p): p is number => p !== undefined);
      const bParents = incoming.get(b.id)!.map(e => prevPositions.get(e.from_step_id)).filter((p): p is number => p !== undefined);
      const aAvg = aParents.length > 0 ? aParents.reduce((s, v) => s + v, 0) / aParents.length : Infinity;
      const bAvg = bParents.length > 0 ? bParents.reduce((s, v) => s + v, 0) / bParents.length : Infinity;
      return aAvg - bAvg || a.sort_order - b.sort_order;
    });
  }

  // --- Coordinate assignment ---
  const positions = new Map<number, NodePosition>(); // step id -> { x, y, w, h }

  let maxLayerWidth = 0;
  for (const layerNodes of layers) {
    let w = 0;
    for (const step of layerNodes) {
      const shape = SHAPES[step.shape] || SHAPES.process;
      w += shape.w;
    }
    w += LAYOUT.hGap * Math.max(0, layerNodes.length - 1);
    maxLayerWidth = Math.max(maxLayerWidth, w);
  }

  let currentY = 0;
  for (let i = 0; i < layers.length; i++) {
    const layerNodes = layers[i];
    let totalW = 0;
    for (const step of layerNodes) {
      const shape = SHAPES[step.shape] || SHAPES.process;
      totalW += shape.w;
    }
    totalW += LAYOUT.hGap * Math.max(0, layerNodes.length - 1);

    // Center the layer
    let x = (maxLayerWidth - totalW) / 2;

    let maxH = 0;
    for (const step of layerNodes) {
      const shape = SHAPES[step.shape] || SHAPES.process;
      positions.set(step.id, { x: x + shape.w / 2, y: currentY + shape.h / 2, w: shape.w, h: shape.h });
      x += shape.w + LAYOUT.hGap;
      maxH = Math.max(maxH, shape.h);
    }
    currentY += maxH + LAYOUT.vGap;
  }

  // --- Compute bounds ---
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [, pos] of positions) {
    minX = Math.min(minX, pos.x - pos.w / 2);
    maxX = Math.max(maxX, pos.x + pos.w / 2);
    minY = Math.min(minY, pos.y - pos.h / 2);
    maxY = Math.max(maxY, pos.y + pos.h / 2);
  }

  // Extra space for back-edge routing on the right side
  const backEdgeCount = backEdges.size;
  const backEdgeSpace = backEdgeCount > 0 ? LAYOUT.backEdgeOffset * (backEdgeCount + 1) : 0;

  const totalW = (maxX - minX) + LAYOUT.padding * 2 + backEdgeSpace;
  const totalH = (maxY - minY) + LAYOUT.padding * 2;
  const originX = minX - LAYOUT.padding;
  const originY = minY - LAYOUT.padding;

  // --- Create SVG ---
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('class', 'flow-svg');
  svg.setAttribute('viewBox', `${originX} ${originY} ${totalW} ${totalH}`);
  container.appendChild(svg);

  // Arrowhead marker
  const defs = document.createElementNS(NS, 'defs');
  const marker = document.createElementNS(NS, 'marker');
  marker.setAttribute('id', 'flow-arrow');
  marker.setAttribute('viewBox', '0 0 10 10');
  marker.setAttribute('refX', '10');
  marker.setAttribute('refY', '5');
  marker.setAttribute('markerWidth', '8');
  marker.setAttribute('markerHeight', '8');
  marker.setAttribute('orient', 'auto-start-reverse');
  const markerPath = document.createElementNS(NS, 'path');
  markerPath.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
  markerPath.setAttribute('fill', COLORS.arrowFill);
  marker.appendChild(markerPath);
  defs.appendChild(marker);

  // Hover arrowhead marker
  const markerHover = marker.cloneNode(true) as SVGMarkerElement;
  markerHover.setAttribute('id', 'flow-arrow-hover');
  (markerHover.querySelector('path') as SVGPathElement).setAttribute('fill', COLORS.arrowHover);
  defs.appendChild(markerHover);

  svg.appendChild(defs);

  const edgeGroup = document.createElementNS(NS, 'g');
  edgeGroup.setAttribute('class', 'flow-edges');
  svg.appendChild(edgeGroup);

  const nodeGroup = document.createElementNS(NS, 'g');
  nodeGroup.setAttribute('class', 'flow-nodes');
  svg.appendChild(nodeGroup);

  // --- Helper: get connection point on a shape ---
  function getPort(stepId: number, side: string): Point {
    const pos = positions.get(stepId)!;
    const step = stepMap.get(stepId)!;
    const shape = step.shape || 'process';

    if (shape === 'decision') {
      // Diamond ports: top, bottom, left, right at the diamond points
      switch (side) {
        case 'top': return { x: pos.x, y: pos.y - pos.h / 2 };
        case 'bottom': return { x: pos.x, y: pos.y + pos.h / 2 };
        case 'left': return { x: pos.x - pos.w / 2, y: pos.y };
        case 'right': return { x: pos.x + pos.w / 2, y: pos.y };
      }
    }

    // Rect/ellipse ports
    switch (side) {
      case 'top': return { x: pos.x, y: pos.y - pos.h / 2 };
      case 'bottom': return { x: pos.x, y: pos.y + pos.h / 2 };
      case 'left': return { x: pos.x - pos.w / 2, y: pos.y };
      case 'right': return { x: pos.x + pos.w / 2, y: pos.y };
    }

    // Fallback (should not reach here)
    return { x: pos.x, y: pos.y };
  }

  // --- Render edges ---
  let backEdgeIdx = 0;

  for (const edge of data.edges) {
    if (!positions.has(edge.from_step_id) || !positions.has(edge.to_step_id)) continue;

    const isBackEdge = backEdges.has(edge.id);
    const fromPos = positions.get(edge.from_step_id)!;
    const toPos = positions.get(edge.to_step_id)!;

    let d: string;
    let labelX: number, labelY: number;

    if (isBackEdge) {
      // Back-edge: route right, then up, then back left
      backEdgeIdx++;
      const rightX = maxX + LAYOUT.backEdgeOffset * backEdgeIdx + LAYOUT.padding / 2;
      const fromPort = getPort(edge.from_step_id, 'right');
      const toPort = getPort(edge.to_step_id, 'right');
      const r = 12; // corner radius

      d = `M ${fromPort.x} ${fromPort.y} `
        + `L ${rightX - r} ${fromPort.y} `
        + `Q ${rightX} ${fromPort.y} ${rightX} ${fromPort.y - r} `
        + `L ${rightX} ${toPort.y + r} `
        + `Q ${rightX} ${toPort.y} ${rightX - r} ${toPort.y} `
        + `L ${toPort.x} ${toPort.y}`;

      labelX = rightX + 6;
      labelY = (fromPort.y + toPort.y) / 2;
    } else {
      // Forward edge: from bottom to top with Bezier
      const fromStep = stepMap.get(edge.from_step_id)!;
      // For decisions, use left/right ports for branches if there are multiple outgoing
      const outEdges = outgoing.get(edge.from_step_id)!.filter(e => !backEdges.has(e.id));
      let fromSide = 'bottom';
      let toSide = 'top';

      if (fromStep.shape === 'decision' && outEdges.length >= 2) {
        const edgeIdx = outEdges.indexOf(edge);
        if (edgeIdx === 0) fromSide = 'left';
        else if (edgeIdx === 1) fromSide = 'right';
        // If more than 2, remaining use bottom
      }

      const fromPort = getPort(edge.from_step_id, fromSide);
      const toPort = getPort(edge.to_step_id, toSide);

      if (fromSide === 'bottom') {
        const midY = (fromPort.y + toPort.y) / 2;
        d = `M ${fromPort.x} ${fromPort.y} C ${fromPort.x} ${midY}, ${toPort.x} ${midY}, ${toPort.x} ${toPort.y}`;
      } else {
        // Side exit from decision: go down a bit, then horizontal, then down to target
        const exitY = fromPort.y + 20;
        d = `M ${fromPort.x} ${fromPort.y} `
          + `L ${fromPort.x} ${exitY} `
          + `L ${toPort.x} ${exitY} `
          + `L ${toPort.x} ${toPort.y}`;
      }

      labelX = (fromPort.x + toPort.x) / 2;
      labelY = (fromPort.y + toPort.y) / 2;
    }

    const g = document.createElementNS(NS, 'g') as SVGGElement;
    g.setAttribute('class', 'flow-edge');
    g.style.cursor = 'default';

    // Hit area
    const hitArea = document.createElementNS(NS, 'path') as SVGPathElement;
    hitArea.setAttribute('d', d);
    hitArea.setAttribute('fill', 'none');
    hitArea.setAttribute('stroke', 'transparent');
    hitArea.setAttribute('stroke-width', '14');
    g.appendChild(hitArea);

    // Visible path
    const pathEl = document.createElementNS(NS, 'path') as SVGPathElement;
    pathEl.setAttribute('d', d);
    pathEl.setAttribute('fill', 'none');
    pathEl.setAttribute('stroke', COLORS.edge);
    pathEl.setAttribute('stroke-width', '1.5');
    pathEl.setAttribute('marker-end', 'url(#flow-arrow)');
    g.appendChild(pathEl);

    // Hover
    g.addEventListener('mouseenter', () => {
      pathEl.setAttribute('stroke', COLORS.edgeHover);
      pathEl.setAttribute('stroke-width', '2.5');
      pathEl.setAttribute('marker-end', 'url(#flow-arrow-hover)');
    });
    g.addEventListener('mouseleave', () => {
      pathEl.setAttribute('stroke', COLORS.edge);
      pathEl.setAttribute('stroke-width', '1.5');
      pathEl.setAttribute('marker-end', 'url(#flow-arrow)');
    });

    edgeGroup.appendChild(g);

    // Edge label
    if (edge.label) {
      const labelGroup = document.createElementNS(NS, 'g');
      labelGroup.setAttribute('class', 'flow-edge-label-group');

      const labelText = document.createElementNS(NS, 'text') as SVGTextElement;
      labelText.setAttribute('x', String(labelX));
      labelText.setAttribute('y', String(labelY));
      labelText.setAttribute('text-anchor', 'middle');
      labelText.setAttribute('dominant-baseline', 'central');
      labelText.setAttribute('fill', COLORS.text);
      labelText.setAttribute('font-family', "'JetBrains Mono', monospace");
      labelText.setAttribute('font-size', '7');
      labelText.textContent = edge.label;
      labelGroup.appendChild(labelText);

      // Measure text and add background
      edgeGroup.appendChild(labelGroup);
      const bbox = labelText.getBBox();
      const bgRect = document.createElementNS(NS, 'rect') as SVGRectElement;
      bgRect.setAttribute('x', String(bbox.x - 4));
      bgRect.setAttribute('y', String(bbox.y - 2));
      bgRect.setAttribute('width', String(bbox.width + 8));
      bgRect.setAttribute('height', String(bbox.height + 4));
      bgRect.setAttribute('rx', '3');
      bgRect.setAttribute('fill', COLORS.labelBg);
      labelGroup.insertBefore(bgRect, labelText);
    }
  }

  // --- Render nodes ---
  for (const step of data.steps) {
    const pos = positions.get(step.id);
    if (!pos) continue;

    const shape = step.shape || 'process';
    const strokeColor = shape === 'start' ? COLORS.startStroke
      : shape === 'decision' ? COLORS.decisionStroke
      : shape === 'end' ? COLORS.endStroke
      : COLORS.nodeStroke;

    const g = document.createElementNS(NS, 'g') as SVGGElement;
    g.setAttribute('class', 'flow-node');
    g.setAttribute('data-step-id', String(step.id));
    g.style.cursor = 'pointer';

    let shapeEl: SVGElement;

    if (shape === 'start' || shape === 'end') {
      // Ellipse
      shapeEl = document.createElementNS(NS, 'ellipse') as SVGEllipseElement;
      shapeEl.setAttribute('cx', String(pos.x));
      shapeEl.setAttribute('cy', String(pos.y));
      shapeEl.setAttribute('rx', String(pos.w / 2));
      shapeEl.setAttribute('ry', String(pos.h / 2));
      shapeEl.setAttribute('fill', COLORS.nodeFill);
      shapeEl.setAttribute('stroke', strokeColor);
      shapeEl.setAttribute('stroke-width', '2');
    } else if (shape === 'decision') {
      // Diamond
      shapeEl = document.createElementNS(NS, 'polygon') as SVGPolygonElement;
      const points = [
        `${pos.x},${pos.y - pos.h / 2}`,     // top
        `${pos.x + pos.w / 2},${pos.y}`,      // right
        `${pos.x},${pos.y + pos.h / 2}`,      // bottom
        `${pos.x - pos.w / 2},${pos.y}`,      // left
      ].join(' ');
      shapeEl.setAttribute('points', points);
      shapeEl.setAttribute('fill', COLORS.nodeFill);
      shapeEl.setAttribute('stroke', strokeColor);
      shapeEl.setAttribute('stroke-width', '2');
    } else {
      // Rectangle
      shapeEl = document.createElementNS(NS, 'rect') as SVGRectElement;
      shapeEl.setAttribute('x', String(pos.x - pos.w / 2));
      shapeEl.setAttribute('y', String(pos.y - pos.h / 2));
      shapeEl.setAttribute('width', String(pos.w));
      shapeEl.setAttribute('height', String(pos.h));
      shapeEl.setAttribute('rx', '8');
      shapeEl.setAttribute('fill', COLORS.nodeFill);
      shapeEl.setAttribute('stroke', strokeColor);
      shapeEl.setAttribute('stroke-width', '2');
    }

    g.appendChild(shapeEl);

    // Label text (supports [[slug]] links)
    const maxChars = shape === 'decision' ? 18 : 26;
    const fontSize = shape === 'decision' ? '7' : '8';
    const text = renderLabelWithLinks(step.label, pos.x, pos.y, fontSize, maxChars, conceptMap);
    g.appendChild(text);

    // Hover effects
    g.addEventListener('mouseenter', () => {
      shapeEl.setAttribute('fill', COLORS.nodeHover);
      shapeEl.setAttribute('stroke-width', '3');
    });
    g.addEventListener('mouseleave', () => {
      shapeEl.setAttribute('fill', COLORS.nodeFill);
      shapeEl.setAttribute('stroke-width', '2');
    });
    g.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
      onStepClick(step);
    });

    nodeGroup.appendChild(g);
  }

  // --- Zoom & Pan (same pattern as treeRenderer) ---
  let scale = 1;
  let vbX = originX;
  let vbY = originY;
  let dragging = false;
  let dragStartX: number, dragStartY: number, vbXStart: number, vbYStart: number;

  function updateViewBox(): void {
    const w = totalW / scale;
    const h = totalH / scale;
    svg.setAttribute('viewBox', `${vbX} ${vbY} ${w} ${h}`);
  }

  function screenToSVG(clientX: number, clientY: number): Point {
    const rect = svg.getBoundingClientRect();
    const vbW = totalW / scale;
    const vbH = totalH / scale;
    const svgX = vbX + (clientX - rect.left) / rect.width * vbW;
    const svgY = vbY + (clientY - rect.top) / rect.height * vbH;
    return { x: svgX, y: svgY };
  }

  svg.addEventListener('wheel', (e: WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.2, Math.min(scale * factor, 5));

    const mouse = screenToSVG(e.clientX, e.clientY);
    const ratio = scale / newScale;
    vbX = mouse.x - (mouse.x - vbX) * ratio;
    vbY = mouse.y - (mouse.y - vbY) * ratio;
    scale = newScale;

    updateViewBox();
  }, { passive: false });

  svg.addEventListener('mousedown', (e: MouseEvent) => {
    if ((e.target as Element).closest('.flow-node')) return;
    dragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    vbXStart = vbX;
    vbYStart = vbY;
    svg.style.cursor = 'grabbing';
  });

  svg.addEventListener('mousemove', (e: MouseEvent) => {
    if (!dragging) return;
    const rect = svg.getBoundingClientRect();
    const vbW = totalW / scale;
    const vbH = totalH / scale;
    const dx = (e.clientX - dragStartX) / rect.width * vbW;
    const dy = (e.clientY - dragStartY) / rect.height * vbH;
    vbX = vbXStart - dx;
    vbY = vbYStart - dy;
    updateViewBox();
  });

  function stopDrag(): void {
    dragging = false;
    svg.style.cursor = 'grab';
  }
  svg.addEventListener('mouseup', stopDrag);
  svg.addEventListener('mouseleave', stopDrag);

  svg.style.cursor = 'grab';

  return {
    highlightSteps(stepIds: number[] | null): void {
      const matchSet = stepIds && stepIds.length > 0 ? new Set(stepIds) : null;

      for (const g of nodeGroup.querySelectorAll('.flow-node') as NodeListOf<SVGGElement>) {
        const id = parseInt(g.getAttribute('data-step-id')!);
        (g as SVGGElement).style.opacity = !matchSet || matchSet.has(id) ? '1' : '0.15';
      }
    },
    destroy(): void {
      container.innerHTML = '';
    },
  };
}
